import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { ArrowLeft } from 'lucide-react';
import MuiLink from '@mui/material/Link';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { User } from '../../../../shared/types/user.types';
import { Vehicle } from '../../../../shared/types/vehicle.types';
import { VehicleTransfer, VehicleTransferStatus } from '../../../../shared/types/vehicle-transfer.types';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import useVehicle from '../../wrappers/use-vehicle';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import DocumentList from '../../components/DocumentList';
import { dealerTransferDetailStyles } from './TransferDetail.styles';

const statusConfig: Record<VehicleTransferStatus, { color: 'warning' | 'success' | 'error' | 'info' | 'default' }> = {
    pending: { color: 'warning' },
    approved: { color: 'success' },
    rejected: { color: 'error' },
    infoRequested: { color: 'info' },
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
    <Box sx={dealerTransferDetailStyles.infoRow}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
        {typeof value === 'string'
            ? <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined}>{value}</Typography>
            : value}
    </Box>
);

const DealerTransferDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { get: getTransfer } = useVehicleTransfer();
    const { get: getVehicle } = useVehicle();
    const [transfer, setTransfer] = useState<VehicleTransfer | null>(null);
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [fromUser, setFromUser] = useState<User | null>(null);
    const [toUser, setToUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!id) return;
        getTransfer(id).then(async (data) => {
            if (!data) { setNotFound(true); setLoading(false); return; }
            setTransfer(data);

            const [v, fu, tu] = await Promise.all([
                getVehicle(data.vehicleUid),
                data.fromUserUid ? getDoc(doc(firestore, 'user', data.fromUserUid)) : null,
                data.toUserUid   ? getDoc(doc(firestore, 'user', data.toUserUid))   : null,
            ]);
            if (v) setVehicle(v);
            if (fu?.exists()) setFromUser({ uid: fu.id, ...fu.data() } as User);
            if (tu?.exists()) setToUser({ uid: tu.id, ...tu.data() } as User);

            setLoading(false);
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <Box sx={dealerTransferDetailStyles.loading}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !transfer) {
        return <Alert severity="error">{t('dealerTransferDetail.notFound')}</Alert>;
    }

    const status = statusConfig[transfer.status ?? 'pending'];

    return (
        <Box sx={dealerTransferDetailStyles.root}>
            <Box sx={dealerTransferDetailStyles.header}>
                <IconButton component={Link} to="/app/service-desk" size="small">
                    <ArrowLeft size={20} />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>{t('dealerTransferDetail.title')}</Typography>
                <Chip label={t(`vehicleTransferStatus.${transfer.status ?? 'pending'}`)} color={status.color} size="small" />
            </Box>

            {/* Stroj */}
            <Paper elevation={0} sx={dealerTransferDetailStyles.sectionPaper}>
                <Box sx={dealerTransferDetailStyles.sectionTitle}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('dealerTransferDetail.machineTitle')}</Typography>
                </Box>
                <InfoRow label={t('dealerTransferDetail.vinLabel')} value={<MuiLink component={Link} to={`/app/service-desk/stroje/${transfer.vehicleUid}`} fontFamily="monospace" variant="body2">{transfer.vehicleUid}</MuiLink>} />
                <Divider />
                <InfoRow label={t('dealerTransferDetail.machineTypeLabel')} value={vehicle?.model ?? '—'} />
            </Paper>

            {/* Majitelé */}
            <Box sx={dealerTransferDetailStyles.ownersGrid}>
                <Paper elevation={0} sx={dealerTransferDetailStyles.sectionPaper}>
                    <Box sx={dealerTransferDetailStyles.sectionTitle}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('dealerTransferDetail.fromOwnerTitle')}</Typography>
                    </Box>
                    <InfoRow label={t('dealerTransferDetail.nameLabel')} value={fromUser?.name ?? '—'} />
                    <Divider />
                    <InfoRow label={t('dealerTransferDetail.emailLabel')} value={fromUser?.email ?? '—'} />
                    <Divider />
                    <InfoRow label={t('dealerTransferDetail.phoneLabel')} value={fromUser?.phone ?? '—'} />
                </Paper>

                <Paper elevation={0} sx={dealerTransferDetailStyles.sectionPaper}>
                    <Box sx={dealerTransferDetailStyles.sectionTitle}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('dealerTransferDetail.toOwnerTitle')}</Typography>
                    </Box>
                    <InfoRow label={t('dealerTransferDetail.nameLabel')} value={transfer.toUserDisplayName ?? toUser?.name ?? '—'} />
                    <Divider />
                    <InfoRow label={t('dealerTransferDetail.phoneLabel')} value={toUser?.phone ?? '—'} />
                </Paper>
            </Box>

            {/* Žádost */}
            <Paper elevation={0} sx={dealerTransferDetailStyles.sectionPaper}>
                <Box sx={dealerTransferDetailStyles.sectionTitle}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('dealerTransferDetail.requestTitle')}</Typography>
                </Box>
                <InfoRow label={t('dealerTransferDetail.requestDate')} value={formatDate(transfer.createdAt)} />
                {transfer.statusNote && (
                    <>
                        <Divider />
                        <InfoRow
                            label={transfer.status === 'rejected' ? t('dealerTransferDetail.rejectionLabel') : t('dealerTransferDetail.infoRequestedLabel')}
                            value={
                                <Typography variant="body2" color={transfer.status === 'rejected' ? 'error.main' : 'info.main'}>
                                    {transfer.statusNote}
                                </Typography>
                            }
                        />
                    </>
                )}
                <Divider />
                <InfoRow label={t('dealerTransferDetail.documentsLabel')} value={<DocumentList filters={{ vehicleTransferUid: id }} />} />
            </Paper>
        </Box>
    );
};

export default DealerTransferDetail;
